import { skipToken } from '@reduxjs/toolkit/query';
import classNames from 'classnames';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCreateGroupMutation,
  useAddGroupToGroupMutation,
  useGetAADWritebackGroupQuery,
  useGetACLGroupRoleMembersQuery,
  useGetACLGroupsQuery,
  useGetACLWritebackMembershipQuery,
  useGetRoleGroupsQuery,
} from '../../graph/services/graphApi';
import { isAlreadyMember } from '../../graph/transientRetry';
import CreationActions from '../../groups/components/CreationActions';
import CreationStatus from '../../groups/components/CreationStatus';
import {
  addWithRetry,
  toMailNickname,
  useGroupCreation,
  type CreationStep,
  type StepReporter,
} from '../../groups/groupCreation';

const DESCRIPTION_PREFIX = '*** Caution: Grants access to confidential resources *** Approver: ';

type Region = 'ANZ' | 'US';

// Project number format differs by region: ANZ carries a leading region letter (6 chars
// before the dot), the US does not (5 chars). Both end with a dot and 2 more chars.
const PROJECT_NUMBER_RULES: Record<
  Region,
  { example: string; leadingCount: number; maxLength: number; regex: RegExp }
> = {
  ANZ: {
    example: '123456.78',
    leadingCount: 6,
    maxLength: 9,
    regex: /^[a-zA-Z0-9]{6}\.[a-zA-Z0-9]{2}$/,
  },
  US: {
    example: '12345.67',
    leadingCount: 5,
    maxLength: 8,
    regex: /^[a-zA-Z0-9]{5}\.[a-zA-Z0-9]{2}$/,
  },
};

// The confidential-project provisioning form body. The button + modal shell live in
// CreateACLGroup; this component renders only the form, step status, and actions, and
// calls onClose when the user cancels or after a successful run finishes navigating.
export default function ProvisionProject({
  onClose,
  onBack,
}: {
  onClose: () => void;
  onBack?: () => void;
}) {
  const navigate = useNavigate();
  const [projectNumber, setProjectNumber] = useState('');
  const [approverName, setApproverName] = useState('');
  const [region, setRegion] = useState<Region>('ANZ');

  const { data: aclGroups, isSuccess: aclGroupsLoaded } = useGetACLGroupsQuery();
  const { data: roleGroups, isSuccess: roleGroupsLoaded } = useGetRoleGroupsQuery();
  const { data: writebackGroup } = useGetAADWritebackGroupQuery();

  const [createGroup] = useCreateGroupMutation();
  const [addGroupToGroup] = useAddGroupToGroupMutation();
  const { steps, isRunning, done, failed, execute } = useGroupCreation();

  // Derived group names
  const aclDisplayName = `ACL_FS_${region}_Projects_${projectNumber}_Modify`;
  const roleDisplayName = `Role_${region}_Project_Contributor_${projectNumber}`;
  const description = `${DESCRIPTION_PREFIX}${approverName}`;

  // Validation
  const rule = PROJECT_NUMBER_RULES[region];
  const projectNumberValid = rule.regex.test(projectNumber);
  const projectNumberHint = `Must be ${rule.leadingCount} alphanumeric characters, a dot, then 2 alphanumeric characters`;
  const approverNameValid = approverName.trim().length > 0;
  // A prior run that failed at step 3/4 leaves the ACL (and maybe the Role) group behind.
  // Find the existing groups by exact displayName so we can tell a finished project from a
  // half-finished one, reuse them rather than create duplicates, and only offer to resume
  // when something is actually missing. Reuse (not re-create) also closes the duplicate-
  // displayName risk: Azure AD permits duplicate displayNames, so a second create is the
  // real hazard. Resolves nothing until the lists have loaded.
  const existingAcl = aclGroups?.find((g) => g.displayName === aclDisplayName);
  const existingRole = roleGroups?.find((g) => g.displayName === roleDisplayName);

  // The ACL existing doesn't mean the project is provisioned — the run could have failed
  // at the nest steps. Read the existing ACL's memberships to tell "done" from "half-done"
  // so a successful project isn't mislabelled as resumable (#87). Skipped (no request)
  // until an existing ACL is found.
  const { data: aclInWriteback, isFetching: writebackFetching } = useGetACLWritebackMembershipQuery(
    existingAcl?.id ?? skipToken
  );
  const { data: aclRoleMembers, isFetching: roleMembersFetching } = useGetACLGroupRoleMembersQuery(
    existingAcl?.id ?? skipToken
  );

  // Completeness can only be judged once both membership reads have settled. When there's
  // no existing ACL there's nothing to check, so this is vacuously ready.
  const membershipChecksReady = !existingAcl || (!writebackFetching && !roleMembersFetching);
  const roleNestedInAcl = !!existingRole && !!aclRoleMembers?.some((g) => g.id === existingRole.id);
  // Fully provisioned: ACL is in the writeback group AND the Role is nested in the ACL.
  const provisionComplete =
    !!existingAcl && membershipChecksReady && aclInWriteback === true && roleNestedInAcl;
  // Something exists but isn't fully wired up — offer to finish the missing steps.
  const canResume = !!existingAcl && membershipChecksReady && !provisionComplete;

  // Require the writeback group up front: without it the nest-into-AAD-Group-Writeback
  // step is guaranteed to fail, but only after the ACL + Role groups have been created —
  // leaving orphaned groups behind. Gate provisioning so those groups are never created
  // (or, on resume, never re-touched) without somewhere to nest them.
  //
  // Also require both group lists (and, on resume, the membership checks) to have settled:
  // a not-yet-loaded list reads as "no existing group", so without this gate a fast submit
  // during load could create a fresh group when it should have reused an existing one (and
  // slip a duplicate displayName past the only client-side guard). A fully-provisioned
  // project has nothing to do, so the button is disabled there too.
  const canProvision =
    projectNumberValid &&
    approverNameValid &&
    !!writebackGroup &&
    aclGroupsLoaded &&
    roleGroupsLoaded &&
    membershipChecksReady &&
    !provisionComplete;

  const handleProvision = () => {
    // Guard against a double-submit racing the disabled state (e.g. a fast second click
    // before React re-renders the button as busy): a second run would create a duplicate.
    if (isRunning || !canProvision) return;

    let aclGroupId = '';
    let roleGroupId = '';

    // On resume the membership write may report the member is already there — that's the
    // idempotent-success case, not a failure, so swallow it and let the step pass.
    const nest = async (groupId: string, memberId: string, reporter: StepReporter) => {
      try {
        await addWithRetry(() => addGroupToGroup({ groupId, memberId }).unwrap(), reporter.onRetry);
      } catch (e) {
        if (!isAlreadyMember(e)) throw e;
      }
    };

    // Create the group, or reuse one left by a prior failed run rather than create a
    // duplicate (#87). assignId hands the resolved id back to the run's closure variable.
    const createOrReuse = (
      kind: 'ACL' | 'Role',
      groupDisplayName: string,
      existing: { id: string } | undefined,
      assignId: (id: string) => void
    ): CreationStep => ({
      errorFallback: `Failed to create ${kind} group`,
      label: existing ? `${kind} group already exists — reusing` : `Create ${kind} group`,
      run: async () => {
        if (existing) {
          assignId(existing.id);
          return;
        }
        const group = await createGroup({
          description,
          displayName: groupDisplayName,
          mailNickname: toMailNickname(groupDisplayName),
        }).unwrap();
        assignId(group.id);
      },
    });

    const creationSteps: CreationStep[] = [
      createOrReuse('ACL', aclDisplayName, existingAcl, (id) => {
        aclGroupId = id;
      }),
      createOrReuse('Role', roleDisplayName, existingRole, (id) => {
        roleGroupId = id;
      }),
      {
        errorFallback: 'Failed to add ACL to AAD Group Writeback',
        label: 'Add ACL to AAD Group Writeback',
        run: async (reporter) => {
          if (!writebackGroup) throw new Error('AAD Group Writeback group could not be resolved');
          // aclGroupId is set by the preceding "Create ACL group" step; the non-null
          // assertion makes that ordering invariant explicit (memberId is `string`, not
          // `string | null`) rather than relying on control-flow narrowing through the closure.
          await nest(writebackGroup.id, aclGroupId!, reporter);
        },
      },
      {
        errorFallback: 'Failed to add Role to ACL group',
        label: 'Add Role to ACL group',
        run: async (reporter) => {
          // aclGroupId / roleGroupId are set by the two preceding create steps; assert the
          // ordering invariant explicitly so memberId stays `string` if these are refactored.
          await nest(aclGroupId!, roleGroupId!, reporter);
        },
      },
    ];

    execute(creationSteps, () => {
      onClose();
      navigate(`/acls/${aclGroupId}`);
    });
  };

  return (
    <>
      <div className="font-bold text-xl mb-4">Provision confidential project</div>

      {!isRunning && !done && !failed && (
        <>
          {/* Region selector */}
          <div className="mb-4">
            {/* Caption for the button group, not a single control — so a div, not a label. */}
            <div className="font-bold mb-1 text-sm">Region</div>
            <div className="flex gap-2">
              {(['ANZ', 'US'] as Region[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={classNames('btn btn-sm w-20', {
                    'btn-outline': region !== r,
                    'btn-primary': region === r,
                  })}
                  onClick={() => setRegion(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Project number input */}
          <div className="mb-4">
            <label htmlFor="provision-project-number" className="block font-bold mb-1 text-sm">
              Project number{' '}
              <span className="font-normal text-base-content/60">(e.g. {rule.example})</span>
            </label>
            <input
              id="provision-project-number"
              type="text"
              className={classNames('input input-bordered w-full', {
                'input-error': projectNumber.length > 0 && !projectNumberValid,
              })}
              placeholder={rule.example}
              value={projectNumber}
              onChange={(e) => setProjectNumber(e.target.value)}
              maxLength={rule.maxLength}
            />
            {projectNumber.length > 0 && !projectNumberValid && (
              <div className="text-error text-xs mt-1">{projectNumberHint}</div>
            )}
          </div>

          {/* Approver name input */}
          <div className="mb-4">
            <label htmlFor="provision-approver-name" className="block font-bold mb-1 text-sm">
              Approver name (project leader)
            </label>
            <input
              id="provision-approver-name"
              type="text"
              className="input input-bordered w-full"
              placeholder="Jane Smith"
              value={approverName}
              onChange={(e) => setApproverName(e.target.value)}
            />
          </div>

          {/* Live preview */}
          {projectNumberValid && (
            <div className="mb-4 bg-base-200 rounded-lg p-3 text-sm">
              <div className="font-bold mb-2">Groups to be created:</div>
              <div className="mb-2">
                <div className="font-mono text-xs mb-1">{aclDisplayName}</div>
                <div className="font-mono text-xs mb-1">{roleDisplayName}</div>
                <div className="text-base-content/70 text-xs mt-2 italic">{description}</div>
              </div>
              <div className="font-bold mt-3 mb-1">Membership changes:</div>
              <div className="text-xs">
                <div>AAD Group Writeback ← {aclDisplayName}</div>
                <div>
                  {aclDisplayName} ← {roleDisplayName}
                </div>
              </div>
            </div>
          )}

          {/* Existing-groups state. Distinguish a finished project (nothing to do) from a
              half-finished one (offer to complete the missing membership steps), and don't
              commit to either until the membership reads settle. */}
          {existingAcl && !membershipChecksReady && (
            <div className="text-base-content/70 mb-4 text-sm py-2">
              Checking existing groups for this project…
            </div>
          )}
          {provisionComplete && (
            <div className="alert alert-success mb-4 text-sm py-2">
              This project is already provisioned.
            </div>
          )}
          {canResume && (
            <div className="alert alert-info mb-4 text-sm py-2">
              Provisioning for this project is incomplete — Resume will reuse the existing groups
              and finish the missing membership steps.
            </div>
          )}

          {/* Writeback group warning */}
          {!writebackGroup && (
            <div className="alert alert-warning mb-4 text-sm py-2">
              AAD Group Writeback group could not be found. Provisioning will fail at step 3.
            </div>
          )}
        </>
      )}

      {(isRunning || done || failed) && (
        <CreationStatus
          steps={steps}
          done={done}
          successMessage="Provisioning complete — navigating to new ACL…"
        />
      )}

      {!done && (
        <CreationActions
          busy={isRunning}
          failed={failed}
          canConfirm={canProvision}
          confirmLabel={canResume ? 'Resume provisioning' : 'Provision'}
          onClose={onClose}
          onConfirm={handleProvision}
          onBack={onBack}
        />
      )}
    </>
  );
}
